import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Download, ArrowLeft, Mail, Phone, MapPin, ExternalLink, Briefcase, GraduationCap, Award, Code } from 'lucide-react';
import { personalInfo, aboutData, skillsData, projectsData } from '../data/mock';

const CVPage = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal-on-scroll').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleDownload = () => {
    alert('CV download functionality will be available when you upload your actual CV file.');
  };

  const experience = [
    {
      title: 'UI/UX Designer',
      company: 'Freelance / Independent',
      period: '2022 - Present',
      location: 'San Francisco, CA',
      highlights: [
        'Led design for 15+ client projects across SaaS, mobile, and web platforms',
        'Increased client conversion rates by an average of 35% through UX optimization',
        'Established design systems that reduced development time by 40%',
      ],
    },
    {
      title: 'Graphic Designer',
      company: 'Freelance',
      period: '2020 - Present',
      location: 'San Francisco, CA',
      highlights: [
        'Created branding, marketing visuals, and digital assets for online businesses and independent clients.',
        'Developed strong visual composition, typography, and layout skills now applied to UI design.',
        'Created and published illustrated storybooks, combining narrative storytelling with visual design to engage young readers.',
      ],
    },
    {
      title: 'Maintenance Engineer',
      company: 'Huaneng Shandong Energy Co.',
      period: '2019 - 2021',
      location: 'Punjab, PK',
      highlights: [
        'Managed maintenance of large-scale power plant systems, identifying failure points and optimizing system reliability.',
        'Developed structured problem-solving and systems-analysis mindset now applied to UX process design.',
      ],
    },
    {
      title: 'HVAC Engineer',
      company: 'Xplor Enterprises',
      period: '2016 - 2018',
      location: 'Islamabad, PK',
      highlights: [
        'Designed and maintained HVAC systems for commercial projects.',
        'Worked with technical constraints, performance metrics, and operational efficiency.',
      ],
    },
  ];

  const awards = [
    'Systems-thinking approach to design, ensuring products scale logically and remain easy to use.',
    'Strong ability to translate complex technical requirements into intuitive interfaces.',
    'Experience working with international clients and remote development teams.',
    'Frontend awareness (HTML, Tailwind, React) enabling smooth design-to-dev handoff.',
  ];

  return (
    <div className="relative">
      <div className="gradient-bg" />
      <div className="grid-bg" />
      <div className="noise-overlay" />

      {/* Header */}
      <section className="pt-32 pb-12">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/50 hover:text-[#00ff88] mb-8 transition-colors group"
          >
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>

          <div 
            className="flex flex-col md:flex-row md:items-center justify-between gap-6"
            style={{ animation: 'fadeUp 1s 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(40px)' }}
          >
            <div>
              <h1 className="text-4xl lg:text-6xl font-bold text-white leading-tight">
                Curriculum Vitae
              </h1>
              <p className="text-white/40 mt-2">Last updated: July 2025</p>
            </div>
            <button
              onClick={handleDownload}
              className="group px-8 py-4 bg-[#00ff88] text-black rounded-full font-semibold hover:shadow-[0_0_40px_rgba(0,255,136,0.4)] transition-all duration-300"
            >
              <span className="flex items-center gap-2">
                <Download size={20} />
                Download PDF
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* CV Content */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div 
            className="glass-card p-8 lg:p-12 space-y-16"
            style={{ animation: 'fadeUp 1s 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(40px)' }}
          >
            {/* Header Info */}
            <div className="flex flex-col md:flex-row gap-8 items-start pb-10 border-b border-white/10">
              <div className="relative">
                <div className="absolute -inset-2 bg-gradient-to-r from-[#00ff88] to-[#7c3aed] rounded-2xl blur opacity-30" />
                <img
                  src={aboutData.image}
                  alt={personalInfo.name}
                  className="relative w-32 h-32 rounded-2xl object-cover"
                />
              </div>
              <div className="flex-1">
                <h2 className="text-3xl font-bold text-white mb-1">{personalInfo.name}</h2>
                <p className="text-[#00ff88] text-lg font-medium mb-4">{personalInfo.role}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { icon: Mail, value: personalInfo.email, href: `mailto:${personalInfo.email}` },
                    { icon: Phone, value: personalInfo.phone, href: `tel:${personalInfo.phone}` },
                    { icon: MapPin, value: personalInfo.location },
                    { icon: ExternalLink, value: 'LinkedIn Profile', href: personalInfo.social.linkedin },
                  ].map((item, index) => (
                    <a
                      key={index}
                      href={item.href}
                      className="flex items-center gap-2 text-white/50 hover:text-[#00ff88] transition-colors text-sm"
                    >
                      <item.icon size={14} />
                      {item.value}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Professional Summary */}
            <div className="reveal-on-scroll stagger-children">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#00ff88]/10 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-[#00ff88]" />
                </div>
                <h3 className="text-sm font-bold text-[#00ff88] uppercase tracking-wider">Professional Summary</h3>
              </div>
              <p className="text-white/70 leading-relaxed">
                {aboutData.bio.split('\n\n')[0]}
              </p>
            </div>

            {/* Experience */}
            <div className="reveal-on-scroll stagger-children">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-[#7c3aed]/10 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-[#7c3aed]" />
                </div>
                <h3 className="text-sm font-bold text-[#7c3aed] uppercase tracking-wider">Professional Experience</h3>
              </div>
              <div className="space-y-10">
                {experience.map((job, index) => (
                  <div key={index} className="relative pl-8 border-l-2 border-white/10 hover:border-[#00ff88]/50 transition-colors">
                    <div className="absolute left-0 top-0 w-3 h-3 rounded-full bg-[#00ff88] -translate-x-[7px]" />
                    <div className="mb-3">
                      <h4 className="text-xl font-bold text-white">{job.title}</h4>
                      <p className="text-[#00ff88] font-medium">{job.company}</p>
                      <p className="text-white/40 text-sm">{job.period} • {job.location}</p>
                    </div>
                    <ul className="space-y-2">
                      {job.highlights.map((highlight, i) => (
                        <li key={i} className="text-white/60 text-sm flex items-start gap-3">
                          <span className="w-1.5 h-1.5 rounded-full bg-white/30 mt-1.5 flex-shrink-0" />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Skills */}
            <div className="reveal-on-scroll stagger-children">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-[#00ff88]/10 flex items-center justify-center">
                  <Code className="w-5 h-5 text-[#00ff88]" />
                </div>
                <h3 className="text-sm font-bold text-[#00ff88] uppercase tracking-wider">Skills & Expertise</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-white font-semibold mb-4">Design Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {skillsData.services.map((service) => (
                      <span
                        key={service.title}
                        className="px-3 py-1.5 bg-white/5 rounded-full text-white/60 text-sm border border-white/10"
                      >
                        {service.title}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-4">Tools</h4>
                  <div className="flex flex-wrap gap-2">
                    {skillsData.tools.map((tool) => (
                      <span
                        key={tool.name}
                        className="px-3 py-1.5 bg-white/5 rounded-full text-white/60 text-sm border border-white/10"
                      >
                        {tool.name}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-4">Strengths</h4>
                  <div className="flex flex-wrap gap-2">
                    {skillsData.strengths.map((strength) => (
                      <span
                        key={strength.name}
                        className="px-3 py-1.5 bg-white/5 rounded-full text-white/60 text-sm border border-white/10"
                      >
                        {strength.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Education */}
            <div className="reveal-on-scroll stagger-children">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#7c3aed]/10 flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-[#7c3aed]" />
                </div>
                <h3 className="text-sm font-bold text-[#7c3aed] uppercase tracking-wider">Education</h3>
              </div>
              <div className="relative pl-8 border-l-2 border-white/10">
                <div className="absolute left-0 top-0 w-3 h-3 rounded-full bg-[#7c3aed] -translate-x-[7px]" />
                <h4 className="text-xl font-bold text-white">Bachelor of Science in Mechanical Engineering</h4>
                <p className="text-[#7c3aed] font-medium">Air University Islamabad</p>
                <p className="text-white/40 text-sm">2010 - 2014 • Islamabad, PK</p>
              </div>
            </div>

            {/* Professional Value */}
            <div className="reveal-on-scroll stagger-children">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#00ff88]/10 flex items-center justify-center">
                  <Award className="w-5 h-5 text-[#00ff88]" />
                </div>
                <h3 className="text-sm font-bold text-[#00ff88] uppercase tracking-wider">Professional Value</h3>
              </div>
              <ul className="space-y-3">
                {awards.map((award, index) => (
                  <li key={index} className="flex items-center gap-3 text-white">
                    <span className="w-2 h-2 rounded-full bg-gradient-to-r from-[#00ff88] to-[#7c3aed]" />
                    {award}
                  </li>
                ))}
              </ul>
            </div>

            {/* Featured Projects */}
            <div className="reveal-on-scroll stagger-children">
              <h3 className="text-sm font-bold text-[#00ff88] uppercase tracking-wider mb-6">Featured Projects</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {projectsData.slice(0, 4).map((project) => (
                  <div
                    key={project.id}
                    className="bg-white/5 rounded-xl p-5 border border-white/10 hover:border-[#00ff88]/30 transition-colors"
                  >
                    <h4 className="text-white font-semibold mb-1">{project.title}</h4>
                    <p className="text-[#00ff88] text-sm mb-2">{project.category}</p>
                    <p className="text-white/50 text-sm line-clamp-2">{project.outcome}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="reveal-on-scroll stagger-children">
            <h2 className="text-3xl font-bold text-white mb-4">Interested in Working Together?</h2>
            <p className="text-white/50 mb-8">Let's discuss how I can contribute to your team or project.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <button className="group px-8 py-4 bg-[#00ff88] text-black rounded-full font-semibold hover:shadow-[0_0_40px_rgba(0,255,136,0.4)] transition-all duration-300">
                  <span className="flex items-center gap-2">
                    Get in Touch
                  </span>
                </button>
              </Link>
              <Link to="/work">
                <button className="px-8 py-4 bg-white/5 border border-white/20 text-white rounded-full font-semibold hover:border-[#00ff88] hover:text-[#00ff88] transition-all">
                  View Portfolio
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CVPage;
